import { useCallback, useEffect, useReducer, useRef } from 'react';

import { getDocs, limit, query, startAfter } from 'firebase/firestore';

import getDocData from '../../firebase/getDocData';
import {
  queryPaginationFetchBatchSuccess,
  queryPaginationFirstFetch,
  queryPaginationFirstFetchError,
  queryPaginationNextFetch,
  queryPaginationNextFetchError,
  queryPaginationReset,
} from './actions';
import { getInitialState, reducer } from './reducer';

const DEFAULT_PAGE_SIZE = 10;

const parseDocs = (snapshot, pageSize) => {
  const lastDoc = snapshot.docs[snapshot.size - 1];
  const docs = {};
  snapshot.docs.forEach(item => {
    const doc = getDocData(item);
    docs[doc.id] = doc;
  });
  return { lastDoc, docs, hasMore: snapshot.size === pageSize };
};

// This hook helps to fetch data in your Firestore database in multiple queries without having to worry
// about state management and pagination. Instead of calling Firestore's getDocs(query(...), () => {}) in a loop
// you simply pass a query list to useFirestoreQueryBatchedPagination() and you get back everything you need,
// including loaded, loading, data, and error.
// use it like so:
// const { data, error, loading, loaded, next } = useFirestoreQueryBatchedPagination(queryRef[], dependencies, pageSize);
// where next is a callback action that you can trigger when you need the next page

// ### YOU MIGHT NEED AN INDEX TO USE THIS ON PRODUCTION ! ###
const useFirestoreQueryBatchedPagination = (
  queryRefList,
  dependencies,
  pageSize = DEFAULT_PAGE_SIZE,
) => {
  const [state, dispatch] = useReducer(reducer, getInitialState());
  const stateRef = useRef();
  const baseQueryByBatchRef = useRef({});
  stateRef.current = state;
  const loading = Object.values(stateRef.current.loadingByBatch).some(i => i);
  const loaded = Object.values(stateRef.current.loadedByBatch).every(i => i);

  const onNextFetchError = useCallback(
    error => dispatch(queryPaginationNextFetchError(error)),
    [dispatch],
  );
  const onNextFetchBatchSuccess = useCallback(
    (batchId, lastDocument, data, hasMore) =>
      dispatch(queryPaginationFetchBatchSuccess(batchId, lastDocument, data, undefined, hasMore)),
    [dispatch],
  );

  const getNextFetch = useCallback(async () => {
    if (loading) {
      return;
    }

    const batchesWithMore = Object.keys(stateRef.current.gotNewDataByBatch).filter(
      batchId =>
        stateRef.current.gotNewDataByBatch[batchId] && stateRef.current.lastDocumentByBatch[batchId],
    );
    if (!batchesWithMore.length) {
      return;
    }

    dispatch(queryPaginationNextFetch(batchesWithMore));
    await Promise.all(
      batchesWithMore.map(async batchId => {
        const currentLastDoc = stateRef.current.lastDocumentByBatch[batchId];
        const baseQuery = baseQueryByBatchRef.current[batchId];
        try {
          const newQuery = query(baseQuery, startAfter(currentLastDoc), limit(pageSize));
          const snapshot = await getDocs(newQuery);
          const { lastDoc, docs, hasMore } = parseDocs(snapshot, pageSize);
          onNextFetchBatchSuccess(batchId, lastDoc, docs, hasMore);
        } catch (error) {
          onNextFetchError(error);
        }
      }),
    );
  }, [loading, pageSize, onNextFetchBatchSuccess, onNextFetchError]);

  const onFirstFetchError = useCallback(
    error => dispatch(queryPaginationFirstFetchError(error)),
    [dispatch],
  );
  const onFirstFetchBatchSuccess = useCallback(
    (batchId, lastDocument, data, hasMore) =>
      dispatch(queryPaginationFetchBatchSuccess(batchId, lastDocument, data, getNextFetch, hasMore)),
    [dispatch, getNextFetch],
  );

  const getFirstFetch = useCallback(async () => {
    dispatch(queryPaginationFirstFetch(queryRefList.length));
    baseQueryByBatchRef.current = {};

    await Promise.all(
      queryRefList.map(async (currentQry, idx) => {
        try {
          baseQueryByBatchRef.current[idx] = currentQry;
          const newQuery = query(currentQry, limit(pageSize));
          const snapshot = await getDocs(newQuery);
          const { lastDoc, docs, hasMore } = parseDocs(snapshot, pageSize);
          onFirstFetchBatchSuccess(idx, lastDoc, docs, hasMore);
        } catch (error) {
          onFirstFetchError(error);
        }
      }),
    );
  }, [dispatch, onFirstFetchError, onFirstFetchBatchSuccess, pageSize, queryRefList]);

  useEffect(() => {
    dispatch(queryPaginationReset());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  useEffect(() => {
    if (!queryRefList.length) {
      return;
    }
    if (!loaded && !loading) {
      getFirstFetch();
    }
  }, [dispatch, getFirstFetch, loaded, loading, queryRefList]);

  const data = [];
  const currentState = stateRef.current;
  Object.values(currentState.dataByBatch).map(batch =>
    Object.values(batch).map(item => data.push(item)),
  );
  const shortState = {
    data,
    error: currentState.error,
    gotNewData: Object.values(currentState.gotNewDataByBatch).some(i => i),
    loaded: Object.values(currentState.loadedByBatch).every(i => i),
    loading: Object.values(currentState.loadingByBatch).some(i => i),
    next: currentState.next,
  };

  return shortState;
};

export default useFirestoreQueryBatchedPagination;
